import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon, CloseCircleIcon, InfoIcon } from '@systemeio/ui/icons';
import { useCourse } from 'modules/courses/courses/hooks/use-course';
import { StudentProgressExportFailReasonEnum } from 'modules/courses/students/enums/student-progress-export-fail-reason-enum';
import { StudentProgressExportStatusEnum } from 'modules/courses/students/enums/student-progress-export-status-enum';
import { useCancelStudentProgressExport } from 'modules/courses/students/hooks/use-cancel-student-progress-export';
import { useStudentProgressExportStatusPolling } from 'modules/courses/students/hooks/use-student-progress-export-status-polling';
import { NotificationStudentProgressExportItemInterface } from 'modules/courses/students/types/student-progress-export-notification-interface';
import { NotificationItemView } from 'modules/notifications/components/notification-item-view';
import { ruleIconSizeClassName } from 'modules/notifications/constants/notification-icons';
import useUser from 'shared/hooks/use-user';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { twMerge } from 'tailwind-merge';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
interface StudentProgressExportNotificationProps {
  notification: NotificationStudentProgressExportItemInterface;
}
const StudentProgressExportNotificationItem = t0 => {
  const $ = _c(5);
  const {
    notification: notificationItem
  } = t0;
  const {
    user
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const {
    cancelStudentProgressExport,
    isCancelling
  } = useCancelStudentProgressExport();
  const t1 = notificationItem.data.status === StudentProgressExportStatusEnum.Running;
  let t2;
  if ($[0] !== notificationItem.data.exportId || $[1] !== t1) {
    t2 = {
      exportId: notificationItem.data.exportId,
      allowFetch: t1
    };
    $[0] = notificationItem.data.exportId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    exportStatus,
    mutate
  } = useStudentProgressExportStatusPolling(t2);
  const notification = {
    ...notificationItem,
    data: {
      ...notificationItem.data,
      ...(exportStatus ?? {})
    }
  };
  const {
    courseData
  } = useCourse(notification.data.courseId ?? undefined);
  const onCancelExport = async () => {
    await cancelStudentProgressExport(notification.data.exportId);
    await mutate(_temp, false);
  };
  const getIcon = () => {
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return <InfoIcon className={twMerge(ruleIconSizeClassName, "fill-yellow")} />;
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return <CheckCircleIcon className={twMerge(ruleIconSizeClassName, "fill-green")} />;
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          return <CloseCircleIcon className={twMerge(ruleIconSizeClassName, "fill-red")} />;
        }
      default:
        {
          return <InfoIcon className={twMerge(ruleIconSizeClassName, "fill-yellow")} />;
        }
    }
  };
  const getMessage = () => {
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return t("dashboard.notifications.student_export.running");
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return t("dashboard.notifications.student_export.success.title");
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case StudentProgressExportFailReasonEnum.CancelledByUser:
              {
                return t("dashboard.notifications.student_export.failed.cancelled_by_user");
              }
            case StudentProgressExportFailReasonEnum.TimeExceeded:
              {
                return t("dashboard.notifications.student_export.failed.time_exceeded");
              }
            case StudentProgressExportFailReasonEnum.RowsNotFound:
              {
                return t("dashboard.notifications.student_export.failed.rows_not_found");
              }
          }
          return notification.message;
        }
      default:
        {
          return notification.message;
        }
    }
  };
  const getDescription = () => {
    const courseName = courseData?.name ? courseData.name : t("dashboard.notifications.student_export.all_courses");
    const info = t("dashboard.notifications.student_export.export_info", {
      courseName
    });
    switch (notification.data.status) {
      case StudentProgressExportStatusEnum.Running:
        {
          return <div className="flex flex-col gap-3"><span>{info}</span><div className="flex flex-col gap-3"><div className="bg-background-tertiary relative h-1 w-full max-w-[342px] rounded-full"><div className="bg-brand-primary absolute left-0 top-0 h-full rounded-full transition-[width] duration-300 ease-in-out" style={{
                  width: `${notification.data.progressPercent}%`
                }} /></div><div className="text-text-primary text-xs font-medium">{notification.data.progressPercent}%</div></div></div>;
        }
      case StudentProgressExportStatusEnum.Failed:
        {
          switch (notification.data.failReason) {
            case StudentProgressExportFailReasonEnum.TimeExceeded:
              {
                return t("dashboard.notifications.student_export.failed.time_exceeded_description");
              }
            case StudentProgressExportFailReasonEnum.RowsNotFound:
              {
                return t("dashboard.notifications.student_export.failed.rows_not_found_description");
              }
            default:
              {
                return null;
              }
          }
        }
      case StudentProgressExportStatusEnum.Success:
        {
          return <div className="flex flex-col gap-3"><span>{info}</span><b>{t("dashboard.notifications.student_export.success.link")}</b>{user && <span>{dateTimeFormat(notification.data.updatedAt, {
                timeFormat: user.timeFormat,
                dateFormat: user.dateFormat,
                timeZone: user.timezone
              })}</span>}</div>;
        }
      default:
        {
          return;
        }
    }
  };
  const t3 = notification.read;
  let t4;
  if ($[3] !== getIcon) {
    t4 = getIcon();
    $[3] = getIcon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return <NotificationItemView isRead={t3} icon={t4} message={getMessage()} description={getDescription()} showCancelButton={notification.data.status === StudentProgressExportStatusEnum.Running} isCancelling={isCancelling} onCancel={onCancelExport} />;
};
export default StudentProgressExportNotificationItem;
function _temp(data) {
  return data && {
    ...data,
    status: StudentProgressExportStatusEnum.Failed,
    failReason: StudentProgressExportFailReasonEnum.CancelledByUser
  };
}